import { SidebarIcon } from '@braskai/icons';
import { Divider, useDeviceType } from '@braskai/ui-kit';
import { Link, useLocation } from 'react-router-dom';

import { Stack } from '@mui/material';

import RaskLogo from '@/shared/assets/images/rask.svg';
import { PATHS } from '@/shared/constants/routes/paths';
import { IS_WHITE_LABEL } from '@/shared/environments';
import { useFeatureFlags } from '@/shared/hooks-tmp/useFeatureFlags';
import { inIframe } from '@/shared/lib/browser';
import { HUBSPOT_VIEW_TYPES, useHubspotView } from '@/shared/thirdParty/hubSpot';

import { DATA_TEST_IDS } from '../../lib/constants';
import { getHelpNavOptions, getNavOptions } from '../../lib/getNavOptions';
import { BannerSidebar } from '../BannerSidebar';
import { NavItem } from '../NavItem';
import { ProfileItem } from '../ProfileItem';
import { SLinkContainer, SList, SSidebarButton, SWhiteLabelIcon, SWhiteLabelLogo } from './styled';

type DesktopNavProps = {
  isOpen?: boolean;
  locked?: boolean;
  handleLocked?: () => void;
};

export const DesktopNav = ({ isOpen, locked, handleLocked }: DesktopNavProps) => {
  const { pathname } = useLocation();

  const { featureVisible: isVoicePresetsFeatureVisible } = useFeatureFlags('voice_presets');
  const { featureVisible: isGlossaryFeatureVisible } = useFeatureFlags('glossary');

  const { isDesktop, isLaptop } = useDeviceType();

  const { handleShowChat } = useHubspotView();

  const showHelpChat = () => handleShowChat(HUBSPOT_VIEW_TYPES.getHelp);

  const navOptions = getNavOptions({
    isGlossaryFeatureVisible,
    isVoicePresetsFeatureVisible,
  });
  const helpNavOptions = getHelpNavOptions({ isWhiteLabel: IS_WHITE_LABEL, showHelpChat });
  const opened = isOpen || locked;
  const logoWidth = IS_WHITE_LABEL ? 32 : 24;

  return (
    <Stack aria-label="navigation sidebar" component="nav" flexGrow={1}>
      <Stack
        alignItems="center"
        direction="row"
        justifyContent="space-between"
        padding={IS_WHITE_LABEL ? '10px 10px 10px 14px' : '10px 10px 10px 18px'}
      >
        <SLinkContainer
          sx={{
            marginRight: opened ? 0 : 16,
            transition: 'width 0.3s',
            width: opened ? '100%' : logoWidth,
          }}
        >
          {IS_WHITE_LABEL ? (
            <Link data-test-id={DATA_TEST_IDS.logoRask} to="/">
              <Stack alignItems="center" direction="row" gap={8}>
                <SWhiteLabelIcon />
                <Stack>
                  <SWhiteLabelLogo variant="body-2-md">Video</SWhiteLabelLogo>
                  <SWhiteLabelLogo variant="body-2-md">Translation Tool</SWhiteLabelLogo>
                </Stack>
              </Stack>
            </Link>
          ) : (
            <Link
              data-test-id={DATA_TEST_IDS.logoRask}
              target={inIframe() ? undefined : '_blank'}
              to={inIframe() ? '/' : 'https://www.rask.ai/'}
            >
              <img alt="Rask" height="24" src={RaskLogo} width="128" />
            </Link>
          )}
        </SLinkContainer>
        {(isLaptop || isDesktop) && (
          <SSidebarButton
            title={locked ? 'Hide sidebar' : 'Lock sidebar'}
            $locked={locked}
            startIcon={<SidebarIcon height={16} width={16} />}
            variant="text"
            onClick={handleLocked}
          />
        )}
      </Stack>
      <SList $flexGrow={1}>
        {navOptions.map(({ title, action, dataTestId, icon, path }) => {
          const selected = pathname === path || (PATHS.homepage === path && pathname.includes(PATHS.folders));

          return (
            <NavItem
              key={title}
              title={title}
              dataTestId={dataTestId}
              icon={icon}
              path={path}
              selected={selected}
              onClick={action}
            />
          );
        })}
      </SList>

      {opened && (
        <Stack margin="0 12px">
          <BannerSidebar />
        </Stack>
      )}

      <SList>
        {helpNavOptions.map(({ title, action, dataTestId, icon }) => (
          <NavItem key={title} title={title} dataTestId={dataTestId} icon={icon} onClick={action} />
        ))}
      </SList>

      <Divider />

      <ProfileItem active={pathname === PATHS.account} />
    </Stack>
  );
};
